
import { Mixins, Component, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
// types
import { ILegacyInvoice } from "@/models/invoices";
import {
  ILegacyInvoiceProviderData,
  ILegacyInvoiceProviderMethods
} from "@/models/providers/legacyInvoices";
import { IProvider } from "@/models/providers";
import { IStatus } from "@/models/statuses";
import { convertObjAb2Json, downloadBlob } from "@/helpers/file";

import _ from "@/boot/lodash";

@Component({
  computed: {
    ...mapState({
      invoiceScope(state, getters) {
        return getters[`data/${DataModules.LEGACY_INVOICES}/scope`](
          this.invoiceId
        );
      },
      invoice(state, getters): ILegacyInvoice {
        return getters["data/single"]({
          id: this.invoiceId,
          scope: this.invoiceScope,
          storeModule: DataModules.LEGACY_INVOICES
        });
      },
      isPaid(state, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isPaid`](
          this.invoice?.content
        );
      },
      isOverdue(state, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isOverdue`](
          this.invoice?.content
        );
      }
    })
  }
})
export default class LegacyInvoiceProvider
  extends Mixins(RequestPromises, Context)
  implements IProvider {
  // Props

  @Prop({ required: true })
  public invoiceId!: ILegacyInvoice["id"];

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    default: () => (vm: LegacyInvoiceProvider) => {
      const name = vm.isAdmin
        ? "adminClientLegacyInvoices"
        : "clientLegacyInvoices";
      return vm.$router.push({ name }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public hasRequestError = false;
  public invoice!: ILegacyInvoice;
  public invoiceScope!: string;
  public isLoading = true;
  public isOverdue!: boolean;
  public isPaid!: boolean;
  public isReloading = false;
  public isProcessing = {};

  // Computed

  public get invNumber() {
    return this.invoice?.content?.number;
  }

  public get invBrandAddress() {
    return this.invoice?.content?.brand?.company_address;
  }

  public get invBrandName() {
    return this.invoice?.content?.brand?.company_name;
  }

  public get invBrandTaxId() {
    return this.invoice?.content?.brand?.vat_number;
  }

  public get invClientName() {
    return this.invoice?.content?.client?.fullname;
  }

  public get invClientCompany() {
    return this.invoice?.content?.client_company?.name;
  }

  public get invClientTaxId() {
    return this.invoice?.content?.client_company?.vat_number;
  }

  public get invClientRegNumber() {
    return this.invoice?.content?.client_company?.reg_number;
  }

  public get invClientAddress() {
    return this.invoice?.content?.client_address;
  }

  public get invDateIssued() {
    return this.invoice?.content?.create_datetime;
  }

  public get invDatePaid() {
    return this.invoice?.content?.paid_datetime;
  }

  public get invItems() {
    return _.map(this.invoice?.content?.products || [], product => ({
      desc: product?.description,
      name: product?.name || product?.product_name,
      quantity: product?.quantity,
      total: product?.net_amount_formatted
    }));
  }

  public get invDiscount() {
    return this.invoice?.content?.net_global_discount_amount_formatted;
  }

  public get invSubtotal() {
    return this.invoice?.content?.net_amount_formatted;
  }

  public get invTax() {
    return this.invoice?.content?.tax_amount_formatted;
  }

  public get invTotal() {
    return this.invoice?.content?.total_amount_formatted;
  }

  public get invAmountPaid() {
    return this.invoice?.content?.paid_amount_formatted;
  }

  public get invAmountCredited() {
    return this.invoice?.content?.partial_amount_credited_formatted;
  }

  public get invAmountDue() {
    return this.invoice?.content?.unpaid_amount_formatted;
  }

  public get invStatus(): IStatus {
    return this.invoice?.content?.status;
  }

  public get isStaged() {
    return !!this.invoice?.staged_import;
  }

  public get isCredited() {
    return !!this.invoice?.content?.partial_amount_credited_converted;
  }

  // Methods

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.loadData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      await this.getInvoiceData(ignoreStored);
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
      if (!this.$store.getters["api/is404"](error)) return;
      this.onNotFound && this.onNotFound(this);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public getInvoiceData(ignoreStored) {
    return this.$store.dispatch(`data/${DataModules.LEGACY_INVOICES}/get`, {
      id: this.invoiceId,
      scope: this.invoiceScope,
      params: {
        with_staged_imports: 1,
        with: ["import.credentials.source"].join(",")
      },
      ignoreStored,
      vm: this
    });
  }

  public download() {
    this.$set(this.isProcessing, "download", true);
    this.$store
      .dispatch(`data/${DataModules.INVOICES_PDFS}/downloadLegacy`, {
        vm: this,
        invoiceId: this.invoiceId
      })
      .then(data => {
        const blob = new Blob([data], {
          type: "application/pdf;"
        });
        const filename = `${this.invoice.number}.pdf`;
        downloadBlob(blob, filename);
      })
      .catch(error => {
        // Convert ArrayBuffer data to JSON
        convertObjAb2Json(error, "response.data");
        // Check for 404 (not found)
        if (this.$_.get(error, "response.status") === 404) {
          return this.$store.dispatch("toast/warning", {
            message: this.$t(
              "_sentence.pdf_is_being_generated_and_will_be_available_shortly"
            )
          });
        }
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.$delete(this.isProcessing, "download");
      });
  }

  // Life-cycle methods

  public async created() {
    if (this.$userCan("show_import_invoice")) return this.loadData();
    this.isLoading = false;
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.LEGACY_INVOICES,
      scope: this.invoiceScope,
      vm: this
    });
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $invoiceData: this.providerData(),
        $invoiceMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("legacyInvoiceProviderData")
  public providerData(): ILegacyInvoiceProviderData {
    return {
      client: this.invoice?.content?.client,
      clientId: this.invoice?.content?.client_id,
      hasRequestError: this.hasRequestError,
      import: this.invoice?.import,
      importId: this.invoice?.import_id,
      invAmountCredited: this.invAmountCredited,
      invAmountDue: this.invAmountDue,
      invAmountPaid: this.invAmountPaid,
      invBrandAddress: this.invBrandAddress,
      invBrandName: this.invBrandName,
      invBrandTaxId: this.invBrandTaxId,
      invClientAddress: this.invClientAddress,
      invClientCompany: this.invClientCompany,
      invClientName: this.invClientName,
      invClientRegNumber: this.invClientRegNumber,
      invClientTaxId: this.invClientTaxId,
      invDateIssued: this.invDateIssued,
      invDatePaid: this.invDatePaid,
      invDiscount: this.invDiscount,
      invItems: this.invItems,
      invNumber: this.invNumber,
      invStatus: this.invStatus,
      invSubtotal: this.invSubtotal,
      invTotal: this.invTotal,
      invTax: this.invTax,
      invoice: this.invoice,
      invoiceId: this.invoiceId,
      isCredited: this.isCredited,
      isLoading: this.isLoading,
      isOverdue: this.isOverdue,
      isPaid: this.isPaid,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      isStaged: this.isStaged
    };
  }

  @Provide("legacyInvoiceProviderMethods")
  public get providerMethods(): ILegacyInvoiceProviderMethods {
    return {
      download: this.download,
      reloadData: this.reloadData
    };
  }
}
